import React, { useMemo } from 'react';
import _isArray from 'lodash-es/isArray';
import _orderBy from 'lodash-es/orderBy';
import {
  Eleve,
  ElevesView,
  ElevesViewField,
} from '@teetsh/app/src/types/eleves';
import { useIsBelowBreakpoint } from '@teetsh/app/src/components/ResponsiveSwitcher/useIsBelowSize';
import ReactTable from '@teetsh/app/src/components/Table/ReactTable';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { useEleveGroups } from '@teetsh/app/src/queries/eleveGroups/useEleveGroups';
import {
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import isEqual from '@teetsh/app/src/utils/time/isEqual';
import sortFctReactTable from '@teetsh/app/src/utils/sortFctReactTable';
import {
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import 'twin.macro';
import { Header } from '@teetsh/app/src/containers/Eleves/List/Table/Header';
import useUpdateElevesView from '@teetsh/app/src/queries/eleves/views/useUpdateElevesView';
import { updateViewWithVisibleFields } from './updateViewWithVisibleFields';
import { viewFieldsToVisibilityState } from './viewFieldsToVisibilityState';
import { orderFieldsInEleveViewFields } from '@teetsh/app/src/containers/Eleves/List/Table/orderFieldsInEleveViewFields';
import { sortFieldsToEleveViewFields } from '@teetsh/app/src/containers/Eleves/List/Table/sortFieldsToEleveViewFields';
import { filterGroupTofFilterArray } from '@teetsh/app/src/containers/Eleves/List/Table/filterGroupToFilterArray';
import { MobileList } from '@teetsh/app/src/containers/Eleves/List/Table/MobileList';
import useDeleteEleve from '@teetsh/app/src/queries/eleves/useDeleteEleve';
import {
  CustomFieldDefinition,
  FieldValueType,
} from '@teetsh/app/src/types/customField';
import { useTranslation } from 'react-i18next';
import { Bool } from './Cells/Bool';
import { RichText } from 'containers/Eleves/List/Table/Cells/RichText';
import { Image } from './Cells/Image';
import { ClassroomCell } from 'containers/Eleves/List/Table/Cells/ClassroomCell';
import { GenderCell } from 'containers/Eleves/List/Table/Cells/GenderCell';
import { useElevesData } from 'queries/eleves/useEleves';
import useUpdateEleve, {
  useUpdateEleveCustomField,
} from 'queries/eleves/useUpdateEleve';
import { DateCell } from 'containers/Eleves/List/Table/Cells/DateCell';
import { TextCell } from 'containers/Eleves/List/Table/Cells/TextCell';
import { FilterBarWrapper } from 'containers/Eleves/List/Table/FilterBar';
import SchoolyearLink from 'components/Link/SchoolyearLink';
import { elevesUrls } from 'containers/Eleves/urls';
import SchoolyearButtonLink from 'components/Buttons/SchoolyearButtonLink';
import { Menu } from 'components/Menu';
import { sortClassrooms } from './sortClassrooms';
import { MultiTagCell } from './Cells/MultiTagCell';
import { NumberCell } from './Cells/NumberCell';
import { PhoneCell } from './Cells/PhoneCell';
import { FilesAndMediaCell } from 'containers/Eleves/List/Table/Cells/FilesAndMediaCell';
import tw from 'twin.macro';
import { Groups } from './Cells/Groups';
import { EleveGroup } from 'types/eleveGroups';
import { MultiselectCell } from './Cells/MultiselectCell';
import { sortGroups } from './sortGroups';

interface Props {
  classrooms: Classroom[];
  view: ElevesView;
  print?: boolean;
  customFields: CustomFieldDefinition[];
}

interface ElevesListProps {
  classrooms: Classroom[];
  eleveGroups: EleveGroup[];
  view: ElevesView;
  print?: boolean;
  customFields: CustomFieldDefinition[];
  eleves: Eleve[];
  updateEleve: (eleve: Partial<Eleve>) => void;
  updateCustomFields: ({
    eleveId,
    customFieldId,
    value,
  }: {
    eleveId: string;
    customFieldId: string;
    value: FieldValueType;
  }) => void;
}

function getAccessorFn(field: ElevesViewField) {
  return item => {
    const value = field.customFieldId
      ? (item.customFields || []).find(c => c.id === field.customFieldId)?.value
      : item[field.id];
    if (field.id === 'groups') {
      return value?.map(g => g.id);
    }
    if (field.fieldType === 'bool') {
      return !!value;
    }
    if (field.fieldType === 'number') {
      return value?.toString();
    }
    return value;
  };
}

export function EleveList(props: ElevesListProps) {
  const {
    classrooms,
    print,
    view,
    eleves,
    updateEleve,
    updateCustomFields,
    eleveGroups,
  } = props;

  const isMobile = useIsBelowBreakpoint('md');
  const { t } = useTranslation(['common', 'eleve']);
  const deleteEleve = useDeleteEleve();
  const updateView = useUpdateElevesView();

  const labelBasicFields = {
    givenName: t('eleve:fields.givenName'),
    familyName: t('eleve:fields.familyName'),
    birthday: t('eleve:fields.birthday'),
    comment: t('eleve:fields.remark'),
    classroomId: t('eleve:fields.classroom'),
    groups: t('eleve:fields.groups'),
    groupName: t('eleve:fields.group'),
    picture: t('eleve:fields.photo'),
    gender: t('eleve:fields.gender'),
    ine: t('eleve:fields.ineShort'),
    radiationDate: t('eleve:fields.radiationDate'),
    arrivalDate: t('eleve:fields.arrivalDate'),
    decloisonement: t('eleve:fields.classeDecloisonnement'),
  };

  function getLabelBasicField(key: string) {
    const fieldName = props.customFields.find(f => f.id === key)?.name;
    return fieldName || labelBasicFields[key] || key;
  }

  const translatedViewFields = [
    ...view.fields.map(f => ({
      ...f,
      fieldName: getLabelBasicField(f.id),
    })),
  ];

  function GoToEleveButton({ id }) {
    return (
      <div tw="pl-4">
        <SchoolyearButtonLink
          to={elevesUrls.eleveDetails(id, view.id)}
          minimal
          icon={PencilSquareIcon}
          large
          iconSize="lg"
        />
      </div>
    );
  }

  const columns = useMemo(
    () => [
      ..._orderBy<ElevesViewField>(translatedViewFields, 'position').map(
        field => {
          const accessorFn = getAccessorFn(field);
          if (field.id === 'decloisonement') {
            // TODO rename decloisonement -> decloisonnement + reprise de données BDD
            return {
              accessorKey: field.id,
              header: () => getLabelBasicField(field.id),
              cell: ({ row }) => {
                const value = row.getValue(field.id);
                return value !== undefined && value !== null
                  ? value || t('common:values.yes') // hack because we store an empty string
                  : value;
              },
              enableColumnFilter: true,
              filterFn: 'arrIncludesSome',
            };
          }
          if (field.id === 'gender') {
            return {
              accessorKey: field.id,
              header: () => getLabelBasicField(field.id),
              cell: p => {
                const value = p.row.getValue(p.column.id);
                const eleve = p.row.original as Eleve;
                return (
                  <GenderCell
                    readonly={print}
                    onChange={newValue =>
                      updateEleve({
                        id: eleve.id,
                        gender: newValue,
                      })
                    }
                    value={value}
                  />
                );
              },
              enableColumnFilter: true,
              filterFn: 'arrIncludesSome',
            };
          }
          if (field.id === 'classroomId') {
            return {
              accessorKey: field.id,
              header: () => getLabelBasicField(field.id),
              cell: p => {
                const value = p.row.getValue(field.id);
                const eleve = p.row.original as Eleve;
                return (
                  <ClassroomCell
                    readonly={print}
                    onChange={v =>
                      updateEleve({
                        id: eleve.id,
                        classroomId: v,
                      })
                    }
                    classrooms={classrooms}
                    value={value}
                  />
                );
              },
              sortingFn: (rowA, rowB, columnId) =>
                sortClassrooms(rowA, rowB, columnId, classrooms),
              enableColumnFilter: true,
              filterFn: 'arrIncludesSome',
            };
          }
          if (field.id === 'familyName' || field.id === 'givenName') {
            return {
              accessorKey: field.id,
              header: () => getLabelBasicField(field.id),
              sortingFn: sortFctReactTable,
              enableColumnFilter: false,
              cell: p => {
                const { id } = p.row.original as Eleve;
                const value = p.row.getValue(field.id);
                return (
                  <SchoolyearLink
                    to={elevesUrls.eleveDetails(id, view.id)}
                    readonly={print}
                    data-testid={`Eleve ${value}`}
                    css={[
                      tw`break-normal text-neutral-intense`,
                      !print && tw`hover:font-semibold cursor-pointer`,
                    ]}
                  >
                    {value}
                  </SchoolyearLink>
                );
              },
              size: 160,
            };
          }
          if (field.id === 'groups') {
            return {
              id: field.id,
              accessorKey: field.id,
              accessorFn,
              header: () => getLabelBasicField(field.id),
              sortingFn: (rowA, rowB, columnId) =>
                sortGroups(rowA, rowB, columnId, eleveGroups),
              filterFn: 'arrIncludesSome',
              enableColumnFilter: true,
              cell: p => {
                const value = p.row.getValue(field.id);
                const eleve = p.row.original as Eleve;

                return (
                  <Groups
                    value={value}
                    readonly={print}
                    eleveGroups={eleveGroups}
                    onChange={v =>
                      updateEleve({
                        id: eleve.id,
                        groups: v,
                      })
                    }
                  />
                );
              },
              size: 160,
            };
          }
          if (field.id === 'ine') {
            return {
              id: field.id,
              accessorFn,
              enableColumnFilter: false,
              header: () => field.fieldName,
              sortingFn: sortFctReactTable,
              cell: p => {
                const value = p.row.getValue(field.id);
                const eleve = p.row.original as Eleve;
                return (
                  <div>
                    <TextCell
                      readonly={print}
                      value={value}
                      exactLength={11}
                      onChange={v =>
                        updateEleve({
                          id: eleve.id,
                          ine: v,
                        })
                      }
                    />
                  </div>
                );
              },
            };
          }
          if (field.id === 'comment') {
            return {
              id: field.id,
              accessorFn,
              header: () => getLabelBasicField(field.id),
              sortingFn: sortFctReactTable,
              enableColumnFilter: false, // not supported yet
              cell: p => {
                const value = p.row.getValue(field.id);
                const eleve = p.row.original as Eleve;

                return (
                  <RichText
                    aria-label={`${eleve.familyName} ${eleve.givenName} comment`}
                    readonly={isMobile || print}
                    value={value}
                    onChange={v =>
                      updateEleve({
                        id: eleve.id,
                        comment: v,
                      })
                    }
                  />
                );
              },
            };
          }

          if (field.fieldType === 'richtext' || field.fieldType === 'text') {
            return {
              id: field.id,
              accessorFn,
              header: () => getLabelBasicField(field.id),
              sortingFn: sortFctReactTable,
              enableColumnFilter: false, // not supported yet
              cell: p => {
                const value = p.row.getValue(field.id);
                const eleve = p.row.original as Eleve;

                return (
                  <RichText
                    aria-label={`${eleve.familyName} ${eleve.givenName} ${field.fieldName}`}
                    readonly={isMobile || print}
                    value={value}
                    onChange={v =>
                      updateCustomFields({
                        eleveId: eleve.id,
                        customFieldId: field.id,
                        value: v,
                      })
                    }
                  />
                );
              },
            };
          }
          if (field.fieldType === 'number') {
            return {
              id: field.id,
              accessorFn,
              header: () => getLabelBasicField(field.id),
              sortingFn: (rowA, rowB, columnId) => {
                const num1 = parseFloat(rowA.getValue(columnId));
                const num2 = parseFloat(rowB.getValue(columnId));
                if (isNaN(num1) || isNaN(num2)) {
                  return 0;
                }
                return num1 - num2;
              },
              enableColumnFilter: true,
              filterFn: 'inNumberRange',
              cell: p => {
                const value = p.row.getValue(field.id);
                const eleve = p.row.original as Eleve;
                return (
                  <NumberCell
                    readonly={print}
                    value={value}
                    onChange={v =>
                      updateCustomFields({
                        eleveId: eleve.id,
                        customFieldId: field.id,
                        value: v,
                      })
                    }
                  />
                );
              },
            };
          }
          if (field.fieldType === 'phone') {
            return {
              id: field.id,
              accessorFn,
              header: () => getLabelBasicField(field.id),
              enableSorting: false,
              enableColumnFilter: false,
              cell: p => {
                const value = p.row.getValue(field.id);
                const eleve = p.row.original as Eleve;
                return (
                  <PhoneCell
                    readonly={print}
                    onChange={v =>
                      updateCustomFields({
                        eleveId: eleve.id,
                        customFieldId: field.id,
                        value: v,
                      })
                    }
                    value={value}
                  />
                );
              },
            };
          }
          if (field.fieldType === 'date') {
            return {
              accessorKey: field.id,
              enableColumnFilter: false,
              header: () => field.fieldName,
              sortingFn: 'datetime',
              cell: ({ row }) => {
                const value = row.getValue(field.id);
                const eleve = row.original as Eleve;
                if (isEqual(value, new Date(1800))) {
                  return null;
                }
                return (
                  <div tw="truncate">
                    <DateCell
                      readonly={print}
                      value={value}
                      onChange={v =>
                        updateEleve({
                          id: eleve.id,
                          [field.id]: v,
                        })
                      }
                    />
                  </div>
                );
              },
            };
          }
          if (field.fieldType === 'select') {
            return {
              id: field.id,
              accessorFn,
              header: () => getLabelBasicField(field.id),
              enableColumnFilter: true,
              filterFn: 'arrIncludesSome',
              cell: p => {
                const fieldId = field.id;
                const value = p.row.getValue(field.id);
                const eleve = p.row.original as Eleve;
                // Mauvais type surement suite à une modification du customfield
                if (typeof value !== 'string' && !_isArray(value) && value) {
                  return <div />;
                }
                return (
                  <MultiTagCell
                    value={value}
                    readonly={print}
                    fieldId={field.id}
                    customFields={props.customFields}
                    onChange={newValue => {
                      updateCustomFields({
                        eleveId: eleve.id,
                        customFieldId: fieldId,
                        value: newValue,
                      });
                    }}
                  />
                );
              },
            };
          }
          if (field.fieldType === 'multiselect') {
            return {
              id: field.id,
              accessorFn,
              header: () => getLabelBasicField(field.id),
              enableColumnFilter: false,
              filterFn: 'arrIncludesSome',
              cell: p => {
                const value = p.row.getValue(field.id);
                const eleve = p.row.original as Eleve;
                return (
                  <MultiselectCell
                    value={value || []}
                    customFields={props.customFields}
                    fieldId={field.id}
                    readonly={print}
                    onChange={newValue => {
                      updateCustomFields({
                        eleveId: eleve.id,
                        customFieldId: field.id,
                        value: newValue,
                      });
                    }}
                  />
                );
              },
            };
          }
          if (field.fieldType === 'bool') {
            return {
              id: field.id,
              accessorFn,
              header: () => getLabelBasicField(field.id),
              enableColumnFilter: true,
              filterFn: 'equals',
              enableSorting: false,
              cell: p => {
                const eleve = p.row.original as Eleve;
                const value = p.row.getValue(field.id);
                return (
                  <Bool
                    aria-label={`${eleve.familyName} ${eleve.givenName} ${field.fieldName}`}
                    readonly={print}
                    onChange={newValue => {
                      updateCustomFields({
                        eleveId: eleve.id,
                        customFieldId: field.id,
                        value: newValue,
                      });
                    }}
                    value={value}
                  />
                );
              },
            };
          }
          if (field.fieldType === 'image') {
            return {
              accessorKey: 'picture',
              header: () => getLabelBasicField(field.id),
              size: 64,
              enableColumnFilter: false,
              cell: p => {
                const eleve = p.row.original as Eleve;
                return (
                  <Image
                    onChange={newValue =>
                      updateEleve({
                        id: eleve.id,
                        picture: newValue,
                      })
                    }
                    readonly={print}
                    value={eleve?.picture}
                  />
                );
              },
            };
          }
          if (field.fieldType === 'filesAndMedia') {
            return {
              id: field.id,
              header: () => getLabelBasicField(field.id),
              accessorFn,
              enableSorting: false,
              enableColumnFilter: false,
              cell: p => {
                const value = p.row.getValue(field.id);
                return (
                  <FilesAndMediaCell
                    small
                    value={value || []}
                    readonly={print}
                    minimal
                    onChange={newFiles => {
                      updateCustomFields({
                        eleveId: p.row.original.id,
                        customFieldId: p.column.id,
                        value: newFiles,
                      });
                    }}
                  />
                );
              },
            };
          }
          throw new Error(
            `Unknown field type : ${field.fieldType} for field ${field.id}`,
          );
        },
      ),
    ],

    [translatedViewFields.map(f => f.id), props.customFields.map(f => f.name)],
  );

  const columnFilters = useMemo(
    () => filterGroupTofFilterArray(view.filters),
    [JSON.stringify(view.filters)],
  );

  const sorting = useMemo(
    () =>
      translatedViewFields.reduce<SortingState>((acc, num) => {
        if (!num?.sort) {
          return acc;
        }
        return [
          ...acc,
          {
            id: num?.id,
            desc: num?.sort?.direction === 'DESC',
          },
        ];
      }, []),
    [
      translatedViewFields
        .map(f => `${f.id} ${JSON.stringify(f.sort)}`)
        .join(''),
      eleveGroups.length, // force recompute when eleveGroups change, as it is used in sorting (otherwise sorting is not applied for groups column)
    ],
  );

  const tableInstance = useReactTable({
    data: eleves,
    columns: columns as any,
    defaultColumn: {
      // permet de définir un même ordre de tri pour toutes les colonnes
      sortDescFirst: false,
    },
    state: {
      sorting,
      columnVisibility: viewFieldsToVisibilityState(translatedViewFields),
      columnOrder: _orderBy(translatedViewFields, 'position').map(
        f => f.fieldName,
      ),
      columnFilters,
    },
    onSortingChange: (sorts: any) =>
      updateView(sortFieldsToEleveViewFields(view, sorts())),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnOrderChange: (order: string[]) => {
      const updatedView = orderFieldsInEleveViewFields(view, order);
      return updateView(updatedView);
    },
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onColumnVisibilityChange: (col: any) => {
      const field = col();
      const key = Object.keys(field)[0];
      return updateView(updateViewWithVisibleFields(key, field[key], view));
    },
    debugTable: false,
  });

  const ff = tableInstance
    .getLeafHeaders()
    .filter(h => h.column.getIsFiltered())
    .map(f => f.id);
  return (
    <div>
      <Header
        customFields={props.customFields}
        print={print}
        viewId={view.id}
        nbOfFilters={tableInstance.getState().columnFilters?.length || 0}
        columns={tableInstance.getAllLeafColumns()}
        onColumnOrderChange={tableInstance.setColumnOrder}
      />
      <FilterBarWrapper
        tableInstance={tableInstance}
        view={view}
        ff={ff}
        classrooms={classrooms}
        eleveGroups={eleveGroups}
        customFields={props.customFields}
        getLabelBasicField={getLabelBasicField}
      />
      <div>
        {isMobile && !print ? (
          <MobileList
            tableInstance={tableInstance}
            action={({ id }) => (
              <div tw="flex justify-center" onClick={e => e.stopPropagation()}>
                <Menu>
                  <Menu.Button large iconSize="lg" minimal tw="px-0">
                    <EllipsisHorizontalIcon tw="h-5 w-5" />
                  </Menu.Button>
                  <Menu.Items>
                    <Menu.Item
                      intent="danger"
                      onClick={() => deleteEleve(id)}
                      text={t('eleve:deleteEleve')}
                      icon={TrashIcon}
                    />
                  </Menu.Items>
                </Menu>
                <div>
                  <SchoolyearButtonLink
                    to={elevesUrls.eleveDetails(id, view.id)}
                    minimal
                    large
                    iconSize="lg"
                    icon={PencilSquareIcon}
                  />
                </div>
              </div>
            )}
          />
        ) : (
          <ReactTable
            hideSort={print}
            condensed={print}
            tableInstance={tableInstance}
            firstCol={print ? undefined : GoToEleveButton}
          />
        )}
      </div>
    </div>
  );
}

// On n'update pas si un élève change, c'est normal c'est pour optimiser la perf
// Car normalement on ne change pas les élèves depuis l'exterieur
// On a mis des states sur le composant
export const ElevesListMemo = React.memo(
  EleveList,
  (p1, p2) =>
    JSON.stringify(p1.customFields) === JSON.stringify(p2.customFields) &&
    p1.eleves.length === p2.eleves.length &&
    JSON.stringify(p1.eleveGroups) === JSON.stringify(p2.eleveGroups) &&
    JSON.stringify(p1.view) === JSON.stringify(p2.view),
);

export function EleveListData(props: Props) {
  const { data: eleves = [] } = useElevesData();
  const updateEleve = useUpdateEleve();
  const updateCustomFields = useUpdateEleveCustomField();
  const { data: eleveGroups = [] } = useEleveGroups();

  return (
    <ElevesListMemo
      eleves={eleves}
      {...props}
      eleveGroups={eleveGroups}
      updateEleve={updateEleve}
      updateCustomFields={updateCustomFields}
    />
  );
}
